import React from 'react'
import {
    Modal,
    ModalBody,
    ModalHeader,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
} from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { Box, Text } from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/react'
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@chakra-ui/react'

import TeamChatMembersEdit from './members'
import TeamChatTitleEdit from './title-edit'
import { useTeamChatConversation } from '../../provider'
import { InternalChatTitle } from '~components/team-chat/layout/sidebar/chat-item'
import {
    namedOperations,
    useDeleteInternalChatMutation,
} from '~graphql/generated/graphql'
import SharedStack from '~components/shared/wrappers/shared-stack'
import ConfirmModal, { ModalType } from '~components/shared/confirm-modal'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { navigate } from 'gatsby'
import { UserRoutes } from '~config/routes'

interface Props {
    isOpen: boolean
    onClose: () => void
}

export default function ManageTeamChatUserModal({ isOpen, onClose }: Props) {
    const { internalChat, internalChatUsers } = useTeamChatConversation()

    const { onCompleted, onError } = useToastFeedback(
        'Successfully deleted team chat.',
        'Error deleting team chat.',
    )
    const {
        isOpen: isConfirmOpen,
        onClose: onConfirmClose,
        onOpen: onConfirmOpen,
    } = useDisclosure()

    const [deleteInternalChat] = useDeleteInternalChatMutation({
        onCompleted: () => {
            onCompleted()
            navigate(UserRoutes.TeamChat)
        },
        onError: onError,
        refetchQueries: [
            namedOperations.Query.queryInternalChatUsers,
            namedOperations.Query.internalChatUsers,
            namedOperations.Query.countInternalChatUsers,
        ],
    })

    async function handleDeleteChat() {
        try {
            await deleteInternalChat({
                variables: {
                    id: internalChat.id,
                },
            })
        } catch (error) {
            console.error('Error deleting internal chat:', error)
        }
        onConfirmClose()
    }

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader
                        borderBottomWidth={1}
                        borderBottomColor="gray.200"
                    >
                        <InternalChatTitle
                            chat={internalChat}
                            users={internalChatUsers}
                            labelOverrides={{
                                fontSize: 'xl',
                                fontWeight: 'bold',
                            }}
                        />
                        <ModalCloseButton />
                    </ModalHeader>
                    <ModalBody py={4}>
                        <TeamChatTitleEdit />
                        <Tabs colorScheme="brand" size="sm" mt={4}>
                            <TabList>
                                <Tab>Members</Tab>
                                <Tab>Settings</Tab>
                            </TabList>

                            <TabPanels>
                                <TabPanel>
                                    <TeamChatMembersEdit />
                                </TabPanel>
                                <TabPanel>
                                    <SharedStack row spaced>
                                        <Box>
                                            <Text
                                                fontWeight="bold"
                                                fontSize="sm"
                                                color="red.500"
                                            >
                                                Delete Team Chat
                                            </Text>
                                            <Text
                                                fontSize="xs"
                                                color="gray.600"
                                            >
                                                The chat will be temporarily
                                                archived and after 30 days all
                                                messages will be deleted.
                                            </Text>
                                            <Text
                                                fontSize="xs"
                                                color="gray.600"
                                            >
                                                To restore the chat, please
                                                reach out to the Chckvet team.
                                            </Text>
                                        </Box>
                                        <Box>
                                            <Button
                                                variant="outline"
                                                colorScheme="red"
                                                px={8}
                                                size="sm"
                                                borderColor="gray.200"
                                                onClick={onConfirmOpen}
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    </SharedStack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <ConfirmModal
                type={ModalType.Delete}
                itemType="team chat"
                isOpen={isConfirmOpen}
                onClose={onConfirmClose}
                onConfirm={handleDeleteChat}
            />
        </>
    )
}
